/* Check-in location — where the church is, and how close counts.
 *
 * The easiest way to get it right is to stand in the building and press
 * "Use my current location". Typing coordinates is there for whoever sets
 * this up from the office.
 */
import { useEffect, useState } from 'react'
import { AlertCircle, Check, Crosshair, MapPin } from 'lucide-react'
import { useChurchPlace, useSetChurchPlace } from '@/api/queries'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { locate } from './CheckIn'

export function LocationSettings({ churchId }: { churchId: string }) {
  const place = useChurchPlace(churchId)
  const save = useSetChurchPlace(churchId)
  const [lat, setLat] = useState('')
  const [lng, setLng] = useState('')
  const [radius, setRadius] = useState('150')
  const [finding, setFinding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    const p = place.data
    if (!p) return
    setLat(p.latitude !== null ? String(p.latitude) : '')
    setLng(p.longitude !== null ? String(p.longitude) : '')
    setRadius(String(p.radius_m || 150))
  }, [place.data])

  async function here() {
    setError(null)
    setFinding(true)
    try {
      const pos = await locate()
      setLat(pos.coords.latitude.toFixed(6))
      setLng(pos.coords.longitude.toFixed(6))
      setSaved(false)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setFinding(false)
    }
  }

  const latN = Number(lat)
  const lngN = Number(lng)
  const radiusN = Number(radius)
  const filled = lat.trim() !== '' && lng.trim() !== ''
  const valid =
    (!filled && lat.trim() === '' && lng.trim() === '') ||
    (filled &&
      Number.isFinite(latN) &&
      Number.isFinite(lngN) &&
      Math.abs(latN) <= 90 &&
      Math.abs(lngN) <= 180)
  const radiusOk = Number.isInteger(radiusN) && radiusN >= 25 && radiusN <= 2000

  function submit() {
    if (!valid || !radiusOk) return
    setError(null)
    setSaved(false)
    save.mutate(
      {
        latitude: filled ? latN : null,
        longitude: filled ? lngN : null,
        radius_m: radiusN,
      },
      {
        onSuccess: () => setSaved(true),
        onError: (err) => setError((err as Error).message),
      },
    )
  }

  const isSet = place.data?.is_set

  return (
    <Card className="space-y-s4 p-s4">
      <div className="flex items-start gap-s3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-pill bg-brand-soft text-brand">
          <MapPin className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 className="text-md font-bold">Check-in location</h2>
          <p className="text-sm text-ink-2">
            {place.isLoading
              ? '…'
              : isSet
                ? 'Members can check in on their phone when they are within this distance.'
                : 'Not set — members can’t check in by themselves until it is. Ushers can still mark people present.'}
          </p>
        </div>
      </div>

      <Button variant="secondary" onClick={here} loading={finding}>
        <Crosshair className="h-4 w-4" aria-hidden />
        Use my current location
      </Button>

      <div className="grid gap-s3 sm:grid-cols-2">
        <Field
          label="Latitude"
          inputMode="decimal"
          value={lat}
          onChange={(e) => {
            setLat(e.target.value)
            setSaved(false)
          }}
          placeholder="13.0827"
        />
        <Field
          label="Longitude"
          inputMode="decimal"
          value={lng}
          onChange={(e) => {
            setLng(e.target.value)
            setSaved(false)
          }}
          placeholder="80.2707"
        />
      </div>
      <Field
        label="How close counts (metres)"
        type="number"
        min={25}
        max={2000}
        value={radius}
        onChange={(e) => {
          setRadius(e.target.value)
          setSaved(false)
        }}
        error={radiusOk ? null : 'Between 25 and 2000 metres.'}
        help="150 metres covers most buildings and their car park."
      />
      {!valid ? (
        <p className="text-sm text-danger">
          Enter both a latitude and a longitude, or leave both empty.
        </p>
      ) : null}

      {filled && valid ? (
        <a
          href={`https://www.openstreetmap.org/?mlat=${latN}&mlon=${lngN}#map=18/${latN}/${lngN}`}
          target="_blank"
          rel="noreferrer"
          className="inline-block text-sm font-semibold text-brand hover:underline"
        >
          Check it on a map
        </a>
      ) : null}

      {error ? (
        <p role="alert" className="flex items-start gap-s2 rounded-md bg-danger-soft px-s3 py-s2 text-base text-danger">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {error}
        </p>
      ) : null}

      <div className="flex items-center justify-end gap-s3">
        {saved ? (
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-success-ink">
            <Check className="h-4 w-4" aria-hidden /> Saved
          </span>
        ) : null}
        <Button variant="primary" onClick={submit} loading={save.isPending} disabled={!valid || !radiusOk}>
          Save
        </Button>
      </div>
    </Card>
  )
}
